// =============================================================================
// Platform game rules, Matchmaking, Socket.IO, Leaderboard
// (a game's own rules — round counts, power-ups, celebrations, its answer
// bonus — live in games/<game>/shared/constants.ts)
// =============================================================================

export const GAME = {
  /** Seconds per turn during gameplay */
  TURN_DURATION_SECONDS: 20,
  /** Seconds to show VS screen after match found */
  VS_SCREEN_HOLD_SECONDS: 2,
  /** Grace period before auto-forfeit on disconnect (seconds) */
  DISCONNECT_GRACE_PERIOD_SECONDS: 15,
  /** Fallback timeout for leave_match if MATCH_END never arrives (ms) */
  LEAVE_MATCH_FALLBACK_MS: 5000,
  /**
   * How long after a match ends the server will still replay its terminal
   * `match_end` to a client that asks for it by id (ms).
   *
   * A backgrounded client misses the live `match_end` entirely: iOS suspends
   * the app, the socket drops, DISCONNECT_GRACE_PERIOD_SECONDS elapses, and
   * the server forfeits and emits the result into a dead socket. On return the
   * match is no longer `InProgress`, so the resync finds nothing and both
   * `rejoin_match` and `leave_match` used to answer MATCH_NOT_FOUND — leaving
   * the player latched on the expired-turn overlay with no way out but killing
   * the app. The replay window is what lets that client learn how its match
   * actually finished.
   *
   * Sized for the realistic "phone call / another app" absence rather than for
   * an indefinite one.
   *
   * Past this window the server answers MATCH_NOT_FOUND and the client has NO
   * automatic recovery — it toasts, and the only exit is the user tapping
   * back/forfeit, which `useForfeit`'s LEAVE_MATCH_FALLBACK_MS then routes
   * home. That is a deliberate limit, not an oversight: an automatic
   * store reset on MATCH_NOT_FOUND would have to fire on every transient
   * not-found, including ones a retry would have healed. If this window is
   * ever shortened, add the automatic reset first — do not leave a longer
   * silent-hang tail behind.
   */
  MATCH_RESULT_REPLAY_WINDOW_MS: 30 * 60 * 1000,
  /**
   * Server-side per-socket rate-limit window for `rejoin_match` (1 event per
   * this many ms). Lives here rather than as a literal in the server's
   * rateLimiter because the CLIENT throttle below is only correct relative to
   * it — a tightened server window would otherwise silently invalidate the
   * client guard with nothing failing.
   */
  REJOIN_MATCH_RATE_LIMIT_WINDOW_MS: 2000,
  /**
   * Minimum gap between two client-initiated `rejoin_match` emits.
   *
   * Must stay comfortably ABOVE `REJOIN_MATCH_RATE_LIMIT_WINDOW_MS`: the
   * server rejects the excess with a RATE_LIMIT error that the client surfaces
   * as a toast, so a burst would show "too many requests" for something the
   * user never did. Bursts are real — iOS fires AppState 'active' on transient
   * interruptions (Control Centre, a notification banner), and a resume can
   * coalesce a frozen turn-stall timer into the same tick as the resume probe.
   */
  REJOIN_MATCH_MIN_INTERVAL_MS: 3000,
  /**
   * Grace period after the client's turn countdown reaches 0 before the client
   * resyncs from the server (ms).
   *
   * `your_turn` / `opponent_turn` are one-shot, unacked, fire-and-forget emits.
   * A wedged downlink swallows one silently, and the server needs up to
   * PING_INTERVAL_MS + PING_TIMEOUT_MS (~25s) to notice — longer than a 20s
   * turn — so no disconnect, no resync, and no recovery replay fires. The
   * client is then left latched on a stale `isYourTurn: true` with an expired
   * countdown: the permanently-stuck "time's up" overlay (#523).
   *
   * On a healthy connection the server's own turn timer fires at the deadline
   * and `round_end` arrives within a network round-trip, so this window must be
   * long enough to let that land (plus TURN_END_HOLD_MS, during which the
   * client intentionally displays the frozen 0) and short enough that a real
   * stall self-heals well inside the player's patience.
   */
  TURN_STALL_RESYNC_GRACE_MS: 4000,
  /** Interval for orphaned match cleanup (ms) */
  ORPHAN_CLEANUP_INTERVAL_MS: 30_000,
  /** Matches InProgress longer than this are considered orphaned (seconds) */
  ORPHAN_MATCH_THRESHOLD_SECONDS: 25 * 60,
} as const;

/**
 * How long a seated table row may sit in `Matchmaking` before a recovery path
 * may treat it as never having started.
 *
 * `startBatakTable` writes the platform row BEFORE the Redis state, on purpose
 * — a crash between the two must leave nothing that claims to be playable. But
 * if the process dies in that window the row is invisible to every recovery
 * path (the sweep enumerates Redis state keys, orphan cleanup is stood down
 * for a lifecycle-owning game), so it sits seated forever and
 * `assertUserAvailable` throws ALREADY_IN_MATCH for all four accounts with no
 * admin path out.
 *
 * The grace is what lets a recovery path reach that row WITHOUT reaching a
 * table that is legitimately mid-start: a start takes milliseconds, so
 * anything still `Matchmaking` after this long had no process behind it.
 */
export const TABLE_START_GRACE_MS = 60_000;

export const GAMEPLAY_FEEDBACK = {
  /** Below this many seconds, the turn timer enters a warning state (amber). */
  TIMER_WARNING_SECONDS: 10,
  /** Below this many seconds, the timer enters an urgent state (red + edge vignette). */
  TIMER_URGENT_SECONDS: 5,
  /** Edge-vignette pulse cycle in ms (one full opacity round-trip). */
  URGENT_PULSE_DURATION_MS: 600,
  /** Peak opacity of the urgent edge vignette overlay. */
  URGENT_VIGNETTE_PEAK_OPACITY: 0.35,
  /**
   * Delay between the server marking a round ended on turn-deadline
   * expiry and emitting `ROUND_END` to clients. Sized to give the loser
   * time to read the "TIME'S UP!" overlay (≈1.5s for two-word headline)
   * — not just paint the final "0". Answer submission is already closed
   * before this hold begins, so the delay never extends the window for
   * accepting input. The winner waits the same duration before the
   * round-transition screen, which is normal end-of-round pacing.
   */
  TURN_END_HOLD_MS: 2000,
  /**
   * Delay between the server emitting `ROUND_END` and starting the next
   * round (which triggers `round_start` → client navigates to
   * QuestionPreview). Sized for a smooth screen transition only — the
   * real "breathe between rounds" pacing comes from QuestionPreview's
   * own countdown. Distinct from `VS_SCREEN_HOLD_SECONDS` which is the
   * match-start VS screen and stays at 2s. Stacking both at 2s here
   * created 4s of dead air after the TIME'S UP overlay.
   */
  INTER_ROUND_TRANSITION_MS: 300,
} as const;

export const MATCHMAKING = {
  /** Maximum time in queue before timeout (seconds) */
  TIMEOUT_SECONDS: 30,
  /** Skill tolerance for pairing (±20% of total wins) */
  SKILL_TOLERANCE_PERCENT: 0.2,
  /** Minimum skill tolerance (absolute wins) */
  MIN_SKILL_TOLERANCE: 5,
  /** Seconds before broadening matchmaking criteria */
  BROADEN_CRITERIA_AFTER_SECONDS: 3,
  /**
   * Seconds before falling back to a bot opponent.
   * MUST stay greater than BROADEN_CRITERIA_AFTER_SECONDS — real players in
   * the queue need at least one broadened poll to pair with each other before
   * a bot claims them. (5s < 10s broadening shipped a regression where two
   * humans queuing together both got bots.)
   */
  BOT_FALLBACK_AFTER_SECONDS: 8,
  /**
   * Bot skill tier for the one-shot guided tutorial match (first-ever
   * join_matchmaking of a brand-new account pairs instantly with a bot of
   * this tier — no queue wait, no energy debit).
   */
  TUTORIAL_BOT_TIER: 'beginner',
  /**
   * Tiers acceptable for the tutorial match when every TUTORIAL_BOT_TIER bot
   * is busy. Deliberately capped at intermediate — a zero-match player must
   * never be handed an advanced/expert opponent; with none of these free the
   * join falls through to the ordinary queue instead.
   */
  TUTORIAL_BOT_FALLBACK_TIERS: ['beginner', 'intermediate'] as const,
  /** Timeout for friend challenge invites (seconds) */
  FRIEND_CHALLENGE_TIMEOUT_SECONDS: 60,
  /** How long friend-table invitees have to answer (#772). */
  FRIEND_TABLE_RESPONSE_SECONDS: 60,
  /** How long the host has to answer "continue without them?" before the table is cancelled. */
  FRIEND_TABLE_DECISION_SECONDS: 60,
  /**
   * Client failsafe past a lobby's deadline: a live lobby always moves on
   * within a worker tick of it, so one still sitting there this long after
   * closed while the device was offline.
   */
  FRIEND_TABLE_CLIENT_GRACE_SECONDS: 5,
  /** Wire bound on `table_invite_create.friendIds` — the widest table any game could seat, minus the host. */
  FRIEND_TABLE_MAX_INVITEES_WIRE: 7,
  /** Matchmaking queue poll interval (ms) */
  POLL_INTERVAL_MS: 1000,
} as const;

/**
 * Staggered bot fill for an N-seat table (#table-bot-seat-stagger).
 *
 * A table whose empty seats were all filled in one instant reads as three
 * bots arriving together, which is the loudest bot tell the game has — the
 * same class as the instant challenge-accept that `BOT_SOCIAL` exists to
 * remove (`07-game-design-rules.md` §Bot Behavior). Seats therefore land one
 * at a time, with a RANDOM gap between them, so the fill reads as people
 * finding the table.
 *
 * The gaps are QUANTISED by the matchmaking worker's own tick
 * (`POLL_INTERVAL_MS`), and this is the part that is easy to get wrong when
 * reading the range below. A seat lands on the first tick at or after its due
 * instant, so two seats are always a whole number of ticks apart: the observed
 * gap is `ceil(drawn / POLL_INTERVAL_MS)` ticks, never the drawn value itself.
 * With the range below and a 1s tick that is 1s, 2s or 3s, weighted roughly
 * 6% / 59% / 35% — measured against the dev server at 2s, 3s / 3s, 2s / 2s, 3s
 * across three fills. Varied, which is the requirement; finer than a tick is
 * not reachable and would not be worth a per-seat timer to reach.
 */
export const TABLE_BOT_FILL = {
  /**
   * Shortest gap between two seats landing. Floored well above the worker's
   * tick so two seats can never share one tick and re-create the very
   * simultaneity this exists to remove.
   */
  MIN_GAP_MS: 900,
  /** Longest gap. Kept short enough that a whole fill stays inside MAX_TOTAL_MS. */
  MAX_GAP_MS: 2_600,
  /**
   * Hard stop on ONE fill, measured from the moment it opened. Past this the
   * remaining seats are filled in a single step — i.e. the fill degrades to
   * exactly the pre-stagger behaviour rather than leaving a player watching
   * an unfinished table. It also bounds how long a filling table holds its
   * players out of the queue's timeout sweep.
   */
  MAX_TOTAL_MS: 9_000,
  /**
   * Re-try delay for a step that found no free bot. Longer than a tick so a
   * saturated pool is re-probed a few times across a fill rather than on
   * every tick — the same starvation reasoning as the 1v1 fallback's `break`.
   */
  NO_BOT_RETRY_MS: 1_500,
} as const;

/**
 * The hand-governing ANNOUNCEMENT on a seated table — the trump, the contract,
 * whatever one seat's choice makes the rule for every card that follows. The
 * choice is shown in the middle of the felt before it settles into its band,
 * and the server holds the table still for `BEAT_MS` after the event (the
 * runtime's `beatMs`, like the trick-collect beat), so no bot plays under the
 * reveal and the next seat's clock starts after it. The
 * client's entrance + hold + flight are derived from this one value
 * (`TABLE_CLIENT.ANNOUNCE_*`), so a retune cannot leave them out of step.
 */
export const TABLE_ANNOUNCE = {
  BEAT_MS: 2_200,
} as const;

export const SOCKET_CONFIG = {
  /**
   * Ping interval for keepalive (ms). Tightened from the Socket.IO default
   * (25s) so half-open sockets are detected faster. Worst-case time for the
   * server to notice a wedged connection is PING_INTERVAL_MS + PING_TIMEOUT_MS
   * (~25s here, down from ~45s). This matters for a 20s-per-turn game: a
   * half-open socket that the server still believes is alive keeps receiving
   * emitted `answer_result` packets into a stalled pipe, so an entire round's
   * events can be swallowed before the socket is declared dead. Kept ≥10s to
   * avoid false disconnects on brief mobile-network jitter (which the 15s
   * disconnect grace period + client reconnect would otherwise absorb anyway).
   */
  PING_INTERVAL_MS: 15_000,
  /** Ping timeout before disconnect (ms). See PING_INTERVAL_MS rationale. */
  PING_TIMEOUT_MS: 10_000,
} as const;

/**
 * The leaderboard is all-time and derives entirely from `User.totalWins` —
 * there is no period bucketing, no reset cycle, and no cached copy. Any
 * constant describing a second win store (TTL, period sentinel, reset
 * cadence) belongs to the retired dual-counter design; do not re-add one
 * without also re-reading the header of `server/src/services/leaderboard.ts`.
 */
export const LEADERBOARD = {
  /** Maximum entries shown on global leaderboard */
  GLOBAL_TOP_LIMIT: 100,
  /** Default page size for leaderboard queries */
  DEFAULT_PAGE_SIZE: 50,
} as const;

/**
 * Achievement catalog. Each entry is a self-contained unlock rule evaluated
 * server-side at match end by `AchievementService` and persisted append-only
 * on `User.achievements` (an array of these string ids). Once earned, an
 * achievement is NEVER revoked even if the underlying stat later regresses
 * (e.g. the win streak is broken, or the player drops out of the top 10).
 *
 * The `id` strings are a stable wire contract shared by server (award path)
 * and client (display). NEVER rename an existing id — to retire one, stop
 * displaying it on the client; the persisted ids stay valid.
 *
 * NOTE: `Speed Demon` (win a Blitz match) is intentionally absent — there is
 * no Blitz mode in Phase 1. `VETERAN` (play 50 matches) takes its display
 * slot until a timed mode ships.
 */
export const ACHIEVEMENTS = {
  /** Win 10 matches in a row (streak resets on any loss or draw). */
  HOT_STREAK: { id: 'hot_streak', winStreakRequired: 10 },
  /** Accumulate 500 lifetime correct answers. */
  WORD_NERD: { id: 'word_nerd', correctAnswersRequired: 500 },
  /** Play 50 lifetime matches. */
  VETERAN: { id: 'veteran', matchesRequired: 50 },
  /** Reach the global all-time leaderboard top 10. */
  CHAMPION: { id: 'champion', leaderboardRankRequired: 10 },
} as const;

/** Stable display order for the achievement grid (matches the catalog above). */
export const ACHIEVEMENT_IDS = [
  ACHIEVEMENTS.HOT_STREAK.id,
  ACHIEVEMENTS.WORD_NERD.id,
  ACHIEVEMENTS.VETERAN.id,
  ACHIEVEMENTS.CHAMPION.id,
] as const;
